<template>
  <SimpleFormModal
    :title="selectedItem.name"
    :submit-button-text="submitButtonText"
    :icon="iconComponent"
    :open="isOpen"
    :disable-submit="submitDisabled"
    @close="handleClose"
    @submit="handleSubmit"
  >
    <template #content>
      <div class="content">
        <div class="section-header-container" @click="openInputSection = !openInputSection">
          <p class="section-header">{{ t('general__text__general-information') }}</p>
          <ChevronToggle
            :state="openInputSection === true ? 'up' : 'down'"
            :medium="true"
            aria-hidden="true"
            class="chevron-toggle"
          />
        </div>
        <section v-if="openInputSection" class="section">
          <TextFieldStandard
            :style="{ width: '50%' }"
            :label="inputs[0].label"
            :prefilledText="inputs[0].prefilledText"
            required
            type="text"
            focus
            hideIconX
            :error="validationResults[inputs[0].key]?.result === false"
            :error-message="'* ' + validationResults[inputs[0].key]?.message"
            @emitInput="(value) => handleInput('name', value)"
          />
          <TextFieldStandard
            v-if="selectedItem.type === 'link'"
            :style="{ width: '50%' }"
            :label="inputs[1].label"
            :prefilledText="inputs[1].prefilledText"
            required
            type="text"
            hideIconX
            :error="validationResults[inputs[1].key]?.result === false"
            :error-message="'* ' + validationResults[inputs[1].key]?.message"
            :help-text="`${t('general__text__valid-format')} 'https://www.example.com'`"
            @emitInput="(value) => handleInput('url', value)"
          />
          <TextAreaStandard
            :label="t('general__text__description')"
            :prefilled-text="selectedItem.description"
            @emitInput="(value) => handleInput('description', value)"
          />
        </section>
        <div class="horizontal-divider"></div>
        <div class="section-header-container" @click="openWaySection = !openWaySection">
          <p class="section-header">{{ t('general__text__directory_path') }}</p>
          <ChevronToggle
            :state="openWaySection === true ? 'up' : 'down'"
            :medium="true"
            aria-hidden="true"
            class="chevron-toggle"
          />
        </div>
        <section v-if="openWaySection" class="section">
          <span class="section-sub-header"> {{ t('general__text__directory_path') }}: </span>
          <span class="section-body">
            <div>
              <template v-for="(breadcrumb, index) in breadcrumbs" :key="index">
                <span class="section-text">
                  <span>{{ breadcrumb.name }} </span
                  ><span v-if="index < breadcrumbs.length - 1">/</span>
                </span>
              </template>
            </div>
            <ButtonText
              :buttonText="t('general__text__move')"
              class="text-button"
              size="small"
              @onClicked="openMove"
            >
              <template #left> <IconMoveFolderSmall /> </template>
            </ButtonText>
          </span>
        </section>
        <div v-if="selectedItem.type !== 'directory'" class="horizontal-divider"></div>
        <div
          v-if="selectedItem.type !== 'directory'"
          class="section-header-container"
          @click="openObjectSection = !openObjectSection"
        >
          <p class="section-header">{{ t('general__text__linked-objects') }}</p>
          <ChevronToggle
            :state="openObjectSection === true ? 'up' : 'down'"
            aria-hidden="true"
            :medium="true"
            class="chevron-toggle"
          />
        </div>
        <section v-if="openObjectSection" class="section">
          <span class="section-sub-header"> {{ t('general__text__linked-objects') }}: </span>

          <BadgeSquare
            v-if="selectedItem && parentNames[selectedItem.id]"
            :badgeText="parentNames[selectedItem.id]"
            :grey="true"
            size="medium"
          />
          <span v-if="!parentNames[selectedItem.id]" class="no-connection-text">
            {{ t('general__text__no-connection') }}
          </span>
          <ButtonText
            :buttonText="t('general__text__change-connection')"
            class="text-button"
            size="small"
            @onClicked="openConnection"
          >
            <template #left> <IconMarkerPin /> </template>
          </ButtonText>
        </section>
      </div>
    </template>
  </SimpleFormModal>
</template>
<script setup lang="ts">
import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import TextAreaStandard from '@/design-system/atoms/TextAreaStandard.vue'
import IconFolderMedium from '@/design-system/assets/icons/IconFolderMedium.vue'
import IconFileMedium from '@/design-system/assets/icons/IconFileMedium.vue'
import IconLinkMedium from '@/design-system/assets/icons/IconLinkMedium.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import SimpleFormModal from './SimpleFormModal.vue'
import { computed, ref, toRefs, watch } from 'vue'
import ChevronToggle from '@/design-system/atoms/ChevronToggle.vue'
import type { DirectoryItem } from './types'
import { useI18n } from 'vue-i18n'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import IconMoveFolderSmall from '@/design-system/assets/icons/IconMoveFolderSmall.vue'
import IconMarkerPin from '@/design-system/assets/icons/IconMarkerPin.vue'
import { validateInput, Input } from './inputValidation'

type Props = {
  open?: boolean
  selectedItem: DirectoryItem
  breadcrumbs: DirectoryItem[]
}

const props = withDefaults(defineProps<Props>(), { open: false })
const { selectedItem } = toRefs(props)
const { t } = useI18n()

const values = ref<Record<string, any>>({})
const initialValues = ref<Record<string, any>>({})
const inputs: Input[] = [
  {
    key: 'name',
    label: t('general__text__name'),
    prefilledText: selectedItem.value.name,
    required: true,
    type: 'text',
    validations: [
      {
        validator: (value) => value !== '',
        message: t('general__text__missing-name')
      },
      {
        validator: (value) => typeof value === 'string' && value.length <= 255,
        message: t('general__data__file-name-too-long')
      }
    ]
  },
  {
    key: 'url',
    label: 'URL',
    prefilledText: selectedItem.value.type === 'link' ? selectedItem.value.url : '',
    required: selectedItem.value.type === 'link',
    type: 'text',
    validations: [
      {
        validator: (value) => value !== '',
        message: `${t('general__text__missing-url')}, ${t(
          'general__text__valid-format'
        ).toLowerCase()} 'https://www.example.com'`
      },
      {
        validator: (value) => {
          const urlPattern = /^(https:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w- ./?%&=]*)?$/i
          return urlPattern.test(value as string)
        },
        message: `${t('general__text__invalid-url')}, ${t(
          'general__text__valid-format'
        ).toLowerCase()} 'https://www.example.com'`
      }
    ],
    helpText: `${t('general__text__valid-format')} 'https://www.example.com'`
  },
  {
    key: 'description',
    label: t('general__text__description'),
    prefilledText: selectedItem.value.description
  }
]
const openInputSection = ref(true)
const openWaySection = ref(false)
const openObjectSection = ref(false)
const iconComponent = ref<any>(null)

const emit = defineEmits<{
  (e: 'submit', item: DirectoryItem, values: Record<string, string | number>): void
  (e: 'close'): void
  (e: 'openConnection'): void
  (e: 'openMove'): void
}>()

const isOpen = ref(props.open)
const parentNames = ref<Record<string, string>>({})
const submitButtonText = computed(() => t(`general__text__update-${selectedItem.value.type}`))
const coreEntityStore = useCoreEntityStore()

const validationResults = computed<Record<string, { result: boolean; message?: string }>>(() => {
  const results: Record<string, { result: boolean; message?: string }> = {}
  inputs.forEach((input) => {
    const value = values.value[input.key]
    if (value !== undefined) {
      const validationResult = validateInput(input, value)
      results[input.key] = validationResult
    } else {
      results[input.key] = { result: true }
    }
  })
  return results
})

async function getCoreEntityNames(path: string[] | undefined): Promise<string | undefined> {
  if (!path || path.length === 0) return undefined
  const names: string[] = []
  for (const id of path) {
    const entity = await coreEntityStore.getCoreEntity(id)
    if (entity) names.push(entity.displayName)
  }
  return names.length ? names.join(', ') : undefined
}

const getIconComponentByType = (type: DirectoryItem['type']) => {
  switch (type) {
    case 'directory':
      return IconFolderMedium
    case 'file':
      return IconFileMedium
    case 'link':
      return IconLinkMedium
    default:
      return IconFileMedium
  }
}

watch(
  selectedItem,
  (item) => {
    if (item) {
      iconComponent.value = getIconComponentByType(item.type)
      values.value = { ...item } as Record<string, any>
      initialValues.value = JSON.parse(JSON.stringify(values.value))
    } else {
      iconComponent.value = null
    }
  },
  { immediate: true }
)

const hasChanges = computed(
  () =>
    Object.values(values.value)
      .filter((v) => (v === null || v === undefined ? '' : v))
      .toSorted()
      .join() !==
    Object.values(initialValues.value)
      .filter((v) => (v === null || v === undefined ? '' : v))
      .toSorted()
      .join()
)

const submitDisabled = computed(() => {
  const missingRequired =
    !values.value.name || (selectedItem.value.type === 'link' && !values.value.url)
  return missingRequired || !hasChanges.value
})

watch(
  () => props.open,
  (val) => {
    isOpen.value = val
  },
  { immediate: true }
)

watch(
  selectedItem,
  async (item) => {
    if (!item) return
    const names = await getCoreEntityNames(item.entityConnectionPath)
    if (names) parentNames.value[item.id] = names
  },
  { immediate: true }
)

const handleInput = (key: string, input: string | number) => {
  values.value[key] = input
}

const handleSubmit = () => {
  if (!submitDisabled.value) {
    isOpen.value = false
    emit('submit', selectedItem.value, values.value)
  }
}

const handleClose = () => {
  openInputSection.value = true
  isOpen.value = false
  emit('close')
}

const openConnection = () => {
  emit('openConnection')
}

const openMove = () => {
  emit('openMove')
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.section-header {
  @include font-mixins.desktop-bold-h5;
  color: #1a1a1a;
}
.section-sub-header {
  @include font-mixins.desktop-bold-paragraph;
  color: #1a1a1a;
}
.section-body {
  @include font-mixins.desktop-regular-paragraph;
  color: #1a1a1a;
  gap: 16px;
  display: flex;
  flex-direction: column;
}
.section-text {
  @include font-mixins.desktop-regular-paragraph;
  color: #1a1a1a;
}
.section-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  cursor: pointer;
}
.section {
  padding: 24px;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #3f46d6;
}
.content {
  width: 800px;
  max-height: 747px;
}
.no-connection-text {
  @include font-mixins.desktop-regular-paragraph;
  color: #767676;
}

.horizontal-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}
.text-button {
  color: #3f46d6 !important;

  &:hover {
    color: #fff !important;
  }
}
.chevron-toggle {
  color: black;
  cursor: pointer;
}
</style>
